import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  type ReactNode,
} from 'react';
import { type Product, sumCents } from '@kon/shared';

export interface CartLine {
  product: Product;
  qty: number;
}

interface CartState {
  lines: CartLine[];
}

type CartAction =
  | { type: 'add'; product: Product; qty: number }
  | { type: 'setQty'; productId: string; qty: number }
  | { type: 'remove'; productId: string }
  | { type: 'clear' }
  | { type: 'hydrate'; state: CartState };

const STORAGE_KEY = 'kon.cart';

function clampQty(qty: number, stock: number): number {
  return Math.max(1, Math.min(qty, Math.max(stock, 1)));
}

function reducer(state: CartState, action: CartAction): CartState {
  switch (action.type) {
    case 'hydrate':
      return action.state;
    case 'add': {
      const existing = state.lines.find((l) => l.product.id === action.product.id);
      if (existing) {
        return {
          lines: state.lines.map((l) =>
            l.product.id === action.product.id
              ? { ...l, qty: clampQty(l.qty + action.qty, action.product.stock) }
              : l,
          ),
        };
      }
      return {
        lines: [...state.lines, { product: action.product, qty: clampQty(action.qty, action.product.stock) }],
      };
    }
    case 'setQty':
      return {
        lines: state.lines.map((l) =>
          l.product.id === action.productId
            ? { ...l, qty: clampQty(action.qty, l.product.stock) }
            : l,
        ),
      };
    case 'remove':
      return { lines: state.lines.filter((l) => l.product.id !== action.productId) };
    case 'clear':
      return { lines: [] };
    default:
      return state;
  }
}

interface CartContextValue {
  lines: CartLine[];
  count: number;
  subtotalCents: number;
  add: (product: Product, qty?: number) => void;
  setQty: (productId: string, qty: number) => void;
  remove: (productId: string) => void;
  clear: () => void;
}

const CartContext = createContext<CartContextValue | null>(null);

function readStored(): CartState {
  if (typeof window === 'undefined') return { lines: [] };
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { lines: [] };
    const parsed = JSON.parse(raw) as CartState;
    if (!Array.isArray(parsed.lines)) return { lines: [] };
    return parsed;
  } catch {
    return { lines: [] };
  }
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, undefined, readStored);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  }, [state]);

  const add = useCallback((product: Product, qty = 1) => dispatch({ type: 'add', product, qty }), []);
  const setQty = useCallback(
    (productId: string, qty: number) => dispatch({ type: 'setQty', productId, qty }),
    [],
  );
  const remove = useCallback((productId: string) => dispatch({ type: 'remove', productId }), []);
  const clear = useCallback(() => dispatch({ type: 'clear' }), []);

  const value = useMemo<CartContextValue>(() => {
    const count = state.lines.reduce((sum, l) => sum + l.qty, 0);
    const subtotalCents = sumCents(state.lines.map((l) => l.product.priceCents * l.qty));
    return { lines: state.lines, count, subtotalCents, add, setQty, remove, clear };
  }, [state.lines, add, setQty, remove, clear]);

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
